import React, { createContext, useContext, ReactNode } from 'react';

/**
 * Tenant configuration for the "Make Room" planner, supplied at the edit-reservation
 * dialog mount. When no upgrade ladder is configured (hasLadder is false) the Make Room
 * button is hidden entirely - there is no fallback ordering.
 */
export interface MakeRoomClientSettings {
    /** True when the tenant has a configured upgrade ladder; gates the Make Room button. */
    hasLadder: boolean;
    /** Room-type keys ordered lowest to highest; upgrade destinations are types above the source. */
    upgradeLadder: string[];
}

export interface ClientSettingsContextType {
    /** Tenant presentation policy; omitted tenants retain their calendar-day labels. */
    programDurationLabel?: (start: Date, end: Date) => string;
    supportsPrintSummaries: boolean;
    paymentPercentOverrides: number[] | null;
    enableGuestRegistration: boolean;
    /**
     * When true, the profile edit modal shows only the form responses in read-only mode,
     * without profile management features (user account status, registration links, etc.).
     * Use this for tenants that just want to display CRM form data without the full
     * guest registration flow.
     */
    guestRegistrationFormsOnly: boolean;
    /**
     * When true, the profile edit modal allows editing and saving of CRM form data
     * directly to the person's SingleFormResponses. This enables staff to update
     * profile information (travel docs, address, contact info) from the reservation view.
     */
    enableProfileDataSave: boolean;
    /**
     * When true, shows the infants field in room booking guest counts.
     * This is disabled by default and can be enabled per-tenant.
     */
    showInfants: boolean;
    /**
     * When true, the current user has manager-level permissions.
     * Enables features like program overbooking override.
     */
    isManager: boolean;
    isSuperAdmin: boolean;
    /**
     * When true, a "Print Booking Confirmation" button is rendered at the end of the
     * Edit Reservation Tools card. The button opens the tenant's
     * `/booking-confirmation/<reservationId>` print page in a new tab. Enable per tenant.
     */
    supportsPrintBookingConfirmation: boolean;
    /**
     * When true, the Open Amount surfaces (rate-plan dialog column and the Bulk Update
     * modal's "Apply Open Amount" section) are shown, letting staff override the computed
     * price with a fixed amount. Disabled by default; enable per tenant.
     */
    enableOpenPrice: boolean;
    /**
     * When true, the POS item dialog lets staff edit the Amount of a package line item
     * (the entered total is distributed across the package's child line items). Disabled by
     * default; enable per tenant.
     */
    enablePackagePriceOverride: boolean;
    /**
     * Make Room planner configuration. Null when the tenant has not configured an
     * upgrade ladder; the Make Room button is only shown when makeRoom.hasLadder is true.
     */
    makeRoom: MakeRoomClientSettings | null;
    /**
     * When true, quoting against a date before today (Bangkok local time) is blocked by
     * default: the room/program/stay date pickers grey out past dates and the save call is
     * rejected server-side. Staff can still opt in per-reservation via the "Allow past dates
     * (backfill)" toggle, available to all quoting staff (no role gate). Disabled by default;
     * enable per tenant.
     */
    blockPastDateQuoting: boolean;
}

const ClientSettingsContext = createContext<ClientSettingsContextType>({
    supportsPrintSummaries: false,
    paymentPercentOverrides: null,
    enableGuestRegistration: false,
    guestRegistrationFormsOnly: false,
    enableProfileDataSave: false,
    showInfants: false,
    isManager: false,
    isSuperAdmin: false,
    supportsPrintBookingConfirmation: false,
    enableOpenPrice: false,
    enablePackagePriceOverride: false,
    makeRoom: null,
    blockPastDateQuoting: false
});

export interface ClientSettingsProviderProps {
    programDurationLabel?: (start: Date, end: Date) => string;
    children: ReactNode;
    supportsPrintSummaries?: boolean;
    paymentPercentOverrides?: number[] | null;
    enableGuestRegistration?: boolean;
    /**
     * When true, the profile edit modal shows only the form responses in read-only mode,
     * without profile management features (user account status, registration links, etc.).
     */
    guestRegistrationFormsOnly?: boolean;
    /**
     * When true, the profile edit modal allows editing and saving of CRM form data
     * directly to the person's SingleFormResponses.
     */
    enableProfileDataSave?: boolean;
    /**
     * When true, shows the infants field in room booking guest counts.
     */
    showInfants?: boolean;
    /**
     * When true, the current user has manager-level permissions.
     */
    isManager?: boolean;
    isSuperAdmin?: boolean;
    /**
     * When true, a "Print Booking Confirmation" button is rendered at the end of the
     * Edit Reservation Tools card.
     */
    supportsPrintBookingConfirmation?: boolean;
    /**
     * When true, the Open Amount surfaces are shown (rate-plan dialog column and Bulk
     * Update modal section). Disabled by default; enable per tenant.
     */
    enableOpenPrice?: boolean;
    /**
     * When true, the POS item dialog lets staff edit the Amount of a package line item.
     * Disabled by default; enable per tenant.
     */
    enablePackagePriceOverride?: boolean;
    /**
     * Make Room planner configuration. Null (or omitted) when the tenant has no
     * upgrade ladder; the Make Room button stays hidden in that case.
     */
    makeRoom?: MakeRoomClientSettings | null;
    /**
     * When true, quoting against a date before today (Bangkok local time) is blocked by
     * default, overridable per-reservation via the "Allow past dates (backfill)" toggle.
     * Disabled by default; enable per tenant.
     */
    blockPastDateQuoting?: boolean;
}

export function ClientSettingsProvider({
    children,
    programDurationLabel,
    supportsPrintSummaries = false,
    paymentPercentOverrides = null,
    enableGuestRegistration = false,
    guestRegistrationFormsOnly = false,
    enableProfileDataSave = false,
    showInfants = false,
    isManager = false,
    isSuperAdmin = false,
    supportsPrintBookingConfirmation = false,
    enableOpenPrice = false,
    enablePackagePriceOverride = false,
    makeRoom = null,
    blockPastDateQuoting = false
}: ClientSettingsProviderProps) {
    return (
        <ClientSettingsContext.Provider value={{
            programDurationLabel,
            supportsPrintSummaries,
            paymentPercentOverrides,
            enableGuestRegistration,
            guestRegistrationFormsOnly,
            enableProfileDataSave,
            showInfants,
            isManager,
            isSuperAdmin,
            supportsPrintBookingConfirmation,
            enableOpenPrice,
            enablePackagePriceOverride,
            makeRoom,
            blockPastDateQuoting
        }}>
            {children}
        </ClientSettingsContext.Provider>
    );
}

export function useClientSettings(): ClientSettingsContextType {
    return useContext(ClientSettingsContext);
}

// Backwards compatibility exports
export const PrintSummaryProvider = ClientSettingsProvider;
export const usePrintSummary = useClientSettings;
export type PrintSummaryContextType = ClientSettingsContextType;
export type PrintSummaryProviderProps = ClientSettingsProviderProps;
